// "New onboarding link" (hub, 2026-10-09): onboard a person who never came in as a lead -- no phone, no consent needed.
//
// It writes ONE row, public.candidates, and starts onboarding for it in the same action:
//   * NO leads row. A leads INSERT fires the recruiter agent's webhook (trigger leads_to_recruiter_agent), and the
//     morning brief / second touch read leads. A candidates INSERT fires nothing (its only trigger is updated_at), and
//     no agent reads candidates -- so nothing here touches any agent, and no agent_* row is ever written.
//   * SMS consent is false by construction: the agents take consent only from the typed leads.sms_consent column, and
//     there is no lead. candidates has no consent column.
//   * source onboarding_manual, as a tag (candidates has no source column): the same convention as "source:manual".
// Then onboarding_start (the allowlisted staff RPC, lib/onboarding.js) mints the link, shown once.
// Guard: an email already on a candidate opens that candidate's Onboarding tab instead of creating a duplicate.
// Nothing is logged but the new candidate id.
import { toE164, NEW_CANDIDATE_DISCIPLINES } from './newCandidate';

export const ONBOARDING_SOURCE = 'onboarding_manual';
export const ONBOARDING_TAG = `source:${ONBOARDING_SOURCE}`;
export const EMPTY_ONBOARDING_LINK = { firstName: '', lastName: '', email: '', discipline: '', phone: '' };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

/** -> { field: message }; {} when the form can be saved. First name, last name and email are required. */
export function validateOnboardingLink(f) {
  const errors = {};
  if (!String(f.firstName || '').trim()) errors.firstName = 'First name is required';
  if (!String(f.lastName || '').trim()) errors.lastName = 'Last name is required';
  if (!EMAIL_RE.test(String(f.email || '').trim())) errors.email = 'Enter a valid email address';
  if (f.discipline && !NEW_CANDIDATE_DISCIPLINES.includes(f.discipline)) errors.discipline = 'Pick a discipline';
  if (String(f.phone || '').trim() && !toE164(f.phone)) errors.phone = 'Enter a US mobile number, or leave it blank';
  return errors;
}

/** The candidates row. Optional fields are blank, never a default that would invent a discipline. */
export function onboardingCandidateRow(f, nowIso) {
  const name = `${String(f.firstName).trim()} ${String(f.lastName).trim()}`;
  return {
    name, email: String(f.email).trim().toLowerCase(), phone: toE164(f.phone) || '',
    specialty: f.discipline || '', state: '', stage: 'New Lead', priority: 'Warm', tags: [ONBOARDING_TAG],
    notes: 'Created in the CRM for onboarding (New onboarding link) · no lead row, no SMS consent',
    created_at: nowIso, updated_at: nowIso,
  };
}

/** The candidate already holding this email (case-insensitive, exact), or null. Candidates only, by ruling. */
export async function findCandidateByEmail(client, email) {
  const e = String(email || '').trim().toLowerCase();
  // ilike for case-insensitive equality; its wildcards (% _) and escape (\) are escaped, so "a_b@x.com" is literal
  const { data, error } = await client.from('candidates').select('*').ilike('email', e.replace(/[\\%_]/g, '\\$&')).limit(1);
  if (error) throw error;
  return (data && data[0]) || null;
}

/**
 * Validate; if the email is already a candidate's, return { existing } and write nothing. Otherwise insert the
 * candidate, start onboarding, and return { candidate, link: { token, expires_at } } -- or { candidate, startError }
 * when the candidate was written but the link could not be started (e.g. a login not on the staff allowlist).
 */
export async function createOnboardingLink({ client, start, form, now = new Date() }) {
  const errors = validateOnboardingLink(form);
  if (Object.keys(errors).length) {
    const err = new Error(Object.values(errors)[0]);
    err.fields = errors;
    throw err;
  }
  const existing = await findCandidateByEmail(client, form.email);
  if (existing) return { existing };
  const { data: candidate, error } = await client.from('candidates').insert(onboardingCandidateRow(form, now.toISOString())).select().single();
  if (error) throw error;
  console.log('onboarding_candidate_created', candidate.id);   // the id only
  try {
    const r = await start(candidate.id);
    return { candidate, link: { token: r.token, expires_at: r.expires_at, session_id: r.session_id } };
  } catch (e) {
    return { candidate, startError: e && e.denied ? 'denied' : (e && e.message) || 'start_failed' };
  }
}
